import EventEmitter from "./EventEmitter.js"
import * as THREE from 'three'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'

/**
 * CLASS: Loader
 * - takes in an array of resources
 * - Resource Format:
 *   {name: type: path: (optional) link: (font type only) text: size: height: texturePath:}
 * - Types: cubeTexture, texture, font
 * 
 * @returns items - list of loaded objects
 */
export default class Loader extends EventEmitter {
    constructor(sources)
    {
        // Construct Base Class
        super()

        // Parameters
        this.sources = sources

        // Set Up
        this.items = {}
        this.toLoad = this.sources.length
        this.loaded = 0

        // Instantiate Methods
        this.setLoaders()
        this.startLoading()
    }

    // Instantiate Loaders
    setLoaders()
    {
        this.loaders = {}
        this.loaders.fontLoader = new FontLoader()
        this.loaders.textureLoader = new THREE.TextureLoader()
        this.loaders.cubeTextureLoader = new THREE.CubeTextureLoader()
    }

    // Start Loading Resources
    startLoading()
    {
        for(const source of this.sources)
        {
            switch(source.type) {
                case 'cubeTexture':
                    this.loaders.cubeTextureLoader.load(
                        source.path,
                        (file) => {
                            file.encoding = THREE.sRGBEncoding
                            this.sourceLoaded(source, file)
                        }
                    )
                    break
                case 'texture':
                    this.loaders.textureLoader.load(
                        source.path,
                        (file) => {
                            file.encoding = THREE.sRGBEncoding
                            this.sourceLoaded(source, file)
                        }
                    )
                    break
                case 'font':
                    this.loaders.textureLoader.load(
                        source.texturePath,
                        (file) => {
                            const textMaterial = new THREE.MeshMatcapMaterial({ matcap: file})
                            this.loaders.fontLoader.load(
                                source.path,
                                (font) => {
                                    const textGeometry = new TextGeometry(
                                        source.text,
                                        {
                                            font: font,
                                            size: source.size,
                                            height: source.height,
                                            curveSegments: 5,
                                            bevelEnabled: true,
                                            bevelThickness: 0.03,
                                            bevelSize: 0.02,
                                            bevelOffset: 0,
                                            bevelSegments: 4
                                        }
                                    )
                                    textGeometry.center()
                                    const text = new THREE.Mesh(textGeometry, textMaterial)
                                    this.sourceLoaded(source, text)
                                }     
                            )
                        }
                    )
                    break
                default:
                    break 
            }
        }
    }

    // Populate Items with Loaded Files
    sourceLoaded(source, file)
    {
        if(source.link != null)
        {
            this.items[source.name] = {
                file: file,
                link: source.link
            }
        }
        else
        {
            this.items[source.name] = file
        }
        this.loaded++

        // Send Ready signal when all items loaded
        if(this.loaded === this.toLoad)
        {
            this.trigger('ready')
        }
    }
}